import { NO_CONTENT } from 'http-status-codes';
import Config from '../../server/config';
import AppError from '../../server/util/AppError';
import {
  getStateAndExtra,
  loginError,
  logoutUser,
  receiveLogin,
} from '../modules/Auth/AuthActions';
import { getCurrentOrgId } from '../modules/Auth/AuthReducer';
import navigationService from './navigationService';

const DETAIL_MESSAGES_FORCING_REFRESH = ['maxAge exceeded', 'jwt expired'];
const shouldRefresh = (json, status) => {
  // Refresh if we get specific error messages and 401 status
  return json && DETAIL_MESSAGES_FORCING_REFRESH.indexOf(json.details) >= 0 && status === 401;
};

const isRequire2FASetupError = (json, status) => {
  // Check if this is a 2FA setup required error
  return (
    status === 401 &&
    json?.message === 'Two-factor authentication setup required before accessing this resource'
  );
};

const handle2FASetupRequired = () => {
  // Save current path so user can return after 2FA setup
  const currentPath =
    typeof window !== 'undefined' ? window.location.pathname + window.location.search : null;

  // Navigate to 2FA setup page with the current path in route state
  // This matches the pattern used in AuthActions.js for 2FA flows
  if (currentPath && currentPath !== '/2fa-setup') {
    navigationService.replace('/2fa-setup', { state: { nextPath: currentPath } });
  } else {
    navigationService.replace('/2fa-setup');
  }
};

// Using dispatch to get orgId - no need for getStore!

export default async function callApi(
  dispatch,
  endpoint,
  token = null, // Keep token parameter for backward compatibility (will be ignored)
  method = 'get',
  data = undefined,
  file = undefined,
  refreshed = false,
) {
  try {
    const headers = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    // Add cookies from the original request if we are in SSR
    const { state, extra } = dispatch(getStateAndExtra());
    if (typeof window === 'undefined' && typeof extra?.req === 'object') {
      headers.Cookie = extra.req.headers?.cookie || '';

      // Pass the original request context through headers for error tracking
      if (extra.req.headers?.referer || extra.req.headers?.referrer) {
        headers['X-Original-Referer'] = extra.req.headers.referer || extra.req.headers.referrer;
      }
      if (extra.req.headers?.['user-agent']) {
        headers['X-Original-User-Agent'] = extra.req.headers['user-agent'];
      }
      if (extra.req.ip || extra.req.connection?.remoteAddress) {
        headers['X-Original-IP'] = extra.req.ip || extra.req.connection.remoteAddress;
      }
      if (extra.req.path) {
        headers['X-Original-Path'] = extra.req.path;
      }
      if (extra.req.method) {
        headers['X-Original-Method'] = extra.req.method;
      }
    }

    // Add x-org header using dispatch
    const orgId = getCurrentOrgId(state);
    if (orgId) {
      headers['x-org'] = orgId;
    }

    let body;
    if (file) {
      body = new FormData();
      body.append('file', file);
      if (data) {
        Object.keys(data).forEach(key => {
          if (typeof data[key] === 'string') {
            body.append(key, data[key]);
          } else {
            body.append(key, JSON.stringify(data[key]));
          }
        });
      }
    } else {
      headers['content-type'] = 'application/json';
      body = JSON.stringify(data);
    }
    // Use the endpoint as-is (caller specifies public/ prefix when needed)
    const resp = await fetch(`${Config.apiUrl}/${endpoint}`, {
      headers,
      method,
      body,
      credentials: 'include', // Include httpOnly cookies
    });
    let json = null;
    let response;
    let error;
    if (resp.status === NO_CONTENT) {
      response = resp;
    } else {
      try {
        json = await resp.json();
        response = resp;
      } catch (err) {
        error = err;
        response = {
          ...resp,
          ok: false,
        };
        json = null;
      }
    }

    if (!response.ok) {
      // Check for 2FA setup required error first
      if (isRequire2FASetupError(json, response.status)) {
        console.log('2FA setup required, redirecting to setup page...');
        handle2FASetupRequired(dispatch);
        return null;
      }

      // Don't attempt token refresh on the refresh endpoint itself
      if (shouldRefresh(json, response.status) && !refreshed && endpoint !== 'auth/refresh') {
        console.log('Token refresh needed, attempting refresh...');

        try {
          // Direct fetch for token refresh using httpOnly cookies
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

          console.log('Calling refresh endpoint...');
          const refreshResponse = await fetch(`${Config.apiUrl}/auth/refresh`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include', // Use httpOnly cookies for refresh
            signal: controller.signal,
          });

          clearTimeout(timeoutId);

          if (!refreshResponse.ok) {
            throw new Error(`Refresh failed: ${refreshResponse.status}`);
          }

          const refreshData = await refreshResponse.json();
          console.log('Token refresh successful, retrying original request...');

          if (refreshData && refreshData.user) {
            dispatch(receiveLogin(refreshData.user));
            // Retry the original API call (cookies will be updated automatically)
            return callApi(dispatch, endpoint, null, method, data, file, true);
          }
          throw new Error('Invalid refresh response format - missing user');
        } catch (refreshErr) {
          if (refreshErr.name === 'AbortError') {
            console.log('Token refresh timed out');
          } else {
            console.log('Token refresh failed:', refreshErr);
          }
          dispatch(logoutUser());
          return null;
        }
      }

      const err =
        error ||
        new AppError({
          status: response.status,
          message: json?.message || 'Error fetching API response',
          errors: json?.errors || [],
        });
      err.extra = {
        json,
        endpoint,
        method,
        body: data,
      };
      err.response = response;
      err.bodyJson = json;
      throw err;
    }
    return json;
  } catch (err) {
    console.log('Error calling API', err);
    if (err.status === 401 && !/^(public\/)?auth(\/|$)/.test(endpoint)) {
      // Check for 2FA setup required error first
      if (isRequire2FASetupError(err.bodyJson, err.status)) {
        console.log('2FA setup required (from catch), redirecting to setup page...');
        handle2FASetupRequired(dispatch);
        return null;
      }

      // Check if this is a deprecated email error and set appropriate error
      if (err.bodyJson?.message?.includes('deprecated')) {
        // Import loginError action to set deprecated email error
        dispatch(loginError('DEPRECATED_EMAIL_ERROR'));
      }
      dispatch(logoutUser());
      return null;
    }
    throw err;
  }
}
